/**
 * The overview (APP_SPEC box 3), worn in Ant Design: one card per
 * project, every number a projection the server counted and a link
 * into the screen that owns it. The "New project" card teaches the
 * first step -- creating a project IS naming one; the first upload
 * mints it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Card, Input, Space, Typography } from 'antd';
import { API_BASE, authFetch } from '../api';
import { CategorySwatch, ComplianceTag } from '../strata';

interface ProjectSummary {
  project_id: string;
  element_count: number;
  elements_by_category: Record<string, number>;
  open_findings: number;
  open_by_severity: Record<string, number>;
  revision_count: number;
  latest_revision: number | null;
  priced_cost: number | null;
  unpriced_lines: number | null;
}

function useProjects() {
  return useQuery<ProjectSummary[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/projects`);
      if (!res.ok) throw new Error(`projects: ${res.status}`);
      return (await res.json()) ?? [];
    },
  });
}

function ProjectCard({ summary }: { summary: ProjectSummary }) {
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: summary.project_id }}
      style={{ textDecoration: 'none' }}
    >
      <Card size="small" hoverable style={{ width: 280 }} title={summary.project_id}>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          {summary.element_count} elements
          {summary.revision_count > 0 && ` · r${summary.latest_revision}`}
        </Typography.Text>
        <Space wrap size="small" style={{ marginBottom: 8 }}>
          {Object.entries(summary.elements_by_category).map(([category, count]) => (
            <span key={category} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
              <CategorySwatch category={category} size={8} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>{count}</Typography.Text>
            </span>
          ))}
        </Space>
        <Space size="middle">
          {summary.open_findings > 0 ? (
            <ComplianceTag state="violating">{summary.open_findings} open findings</ComplianceTag>
          ) : (
            <ComplianceTag state="complying">✓ no open findings</ComplianceTag>
          )}
          {summary.priced_cost !== null && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              ${summary.priced_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              {summary.unpriced_lines ? ` (+${summary.unpriced_lines} unpriced)` : ''}
            </Typography.Text>
          )}
        </Space>
      </Card>
    </Link>
  );
}

export function Overview() {
  const { data: projects = [], isLoading } = useProjects();
  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const openNew = () => {
    const name = newName.trim();
    if (name)
      void navigate({ to: '/projects/$projectId', params: { projectId: name } });
  };

  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Space align="baseline" size="large">
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Projects
        </Typography.Title>
        <Link to="/rules">Rules ingestion →</Link>
        <Link to="/onboard">Onboard organization →</Link>
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
        {isLoading ? 'Counting…' : `${projects.length} on the server`}
      </Typography.Paragraph>
      <Space wrap size="middle" align="start">
        {projects.map((summary) => (
          <ProjectCard key={summary.project_id} summary={summary} />
        ))}
        <Card size="small" title="New project" style={{ width: 280 }}>
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onPressEnter={openNew}
              placeholder="name it"
              aria-label="New project name"
            />
            <Button type="primary" onClick={openNew} disabled={!newName.trim()}>
              Open workspace
            </Button>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              The first upload creates the project.
            </Typography.Text>
          </Space>
        </Card>
      </Space>
    </div>
  );
}
